import React, {
	createContext,
	useContext,
	useCallback,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
	type Dispatch,
	type SetStateAction,
} from 'react';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { createAri } from '@atlassian/jira-platform-ari';
import type { PickedProject } from '@atlassian/jira-project-picker/src/model';
import { messages } from '@atlassian/jira-shared-software-dates';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import {
	CONNECT_JSW_PROJECT_EXPERIENCE,
	DISCONNECT_JSW_PROJECT_EXPERIENCE,
} from '../../common/constants';
import {
	setProjectProperty,
	getProjectProperty,
	deleteProjectProperty,
} from '../../services/project-properties';
import type { ConnectedProject } from '../../types';
import { ProjectAction } from './constants';

type State = {
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	connectedProject: ConnectedProject | null | void;
	loading: boolean;
	error: Error | null;
};

type Context = State & {
	setContext: Dispatch<SetStateAction<State>>;
};

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
const ConnectedProjectContext = createContext<Context>(null as any);

export const ConnectedProjectProvider = ({ children }: { children: ReactNode }) => {
	const [state, setState] = useState<State>({
		connectedProject: undefined,
		loading: true,
		error: null,
	});

	const value = useMemo(() => ({ ...state, setContext: setState }), [state]);

	return (
		<ConnectedProjectContext.Provider value={value}>{children}</ConnectedProjectContext.Provider>
	);
};

export const useConnectedProject = (projectId: string) => {
	const { connectedProject, error, loading, setContext } = useContext(ConnectedProjectContext);

	useEffect(() => {
		// Fetch the connected project if not fetched already
		if (connectedProject === undefined) {
			getProjectProperty<ConnectedProject>({ projectKeyOrId: projectId })
				.then((project) =>
					setContext({
						connectedProject: project.value ?? null,
						loading: false,
						error: null,
					}),
				)
				.catch((err) =>
					setContext({
						connectedProject: null,
						loading: false,
						error: err,
					}),
				);
		}
	}, [connectedProject, projectId, setContext]);

	return {
		connectedProject,
		error,
		loading,
	};
};

export const useChangeConnectedProject = (projectId: string) => {
	const { formatMessage } = useIntl();
	const { connectedProject, loading, error, setContext } = useContext(ConnectedProjectContext);

	const cloudId = useCloudId();

	const startConnectExperience = useExperienceStart(CONNECT_JSW_PROJECT_EXPERIENCE);
	const onSuccessConnect = useExperienceSuccess(CONNECT_JSW_PROJECT_EXPERIENCE);
	const onFailConnect = useExperienceFail(CONNECT_JSW_PROJECT_EXPERIENCE);

	const startDisconnectExperience = useExperienceStart(DISCONNECT_JSW_PROJECT_EXPERIENCE);
	const onSuccessDisconnect = useExperienceSuccess(DISCONNECT_JSW_PROJECT_EXPERIENCE);
	const onFailDisconnect = useExperienceFail(DISCONNECT_JSW_PROJECT_EXPERIENCE);

	const { showFlag } = useFlagService();

	const connectProject = useCallback(
		async (selectedProject: PickedProject) => {
			const isSwitching = connectedProject != null;

			const projectAri = createAri({
				resourceOwner: 'jira',
				cloudId,
				resourceType: 'project',
				resourceId: selectedProject?.details?.id ?? '',
			});

			// Check that this is a different project to the currently selected one.
			if (projectAri === connectedProject?.resourceId) {
				return undefined;
			}

			// Connect selected project.
			const project = {
				id: selectedProject?.details?.id ?? '',
				name: selectedProject.name,
				resourceId: projectAri,
			};

			startConnectExperience();
			setContext({
				connectedProject: null,
				loading: true,
				error: null,
			});

			try {
				await setProjectProperty({
					propertyValue: project,
					projectKeyOrId: projectId,
				});

				setContext({
					loading: false,
					error: null,
					connectedProject: project,
				});

				onSuccessConnect();

				return { project, isSwitching, action: ProjectAction.CONNECT };
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setContext({
					loading: false,
					error: err,
					connectedProject: null,
				});
				onFailConnect('Failed to connect project', err);

				throw err;
			}
		},
		[
			cloudId,
			connectedProject,
			onFailConnect,
			onSuccessConnect,
			projectId,
			setContext,
			startConnectExperience,
		],
	);

	const disconnectProject = useCallback(async () => {
		startDisconnectExperience();

		try {
			await deleteProjectProperty({ projectKeyOrId: projectId });
			setContext({ loading: false, error: null, connectedProject: null });
			onSuccessDisconnect();

			return {
				project: connectedProject,
				action: ProjectAction.DISCONNECT,
			};
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			if (err.statusCode === 404) {
				onSuccessDisconnect();
				showFlag({
					type: 'error',
					title: formatMessage(messages.projectConnectionNotFoundTitle),
					description: formatMessage(messages.projectConnectionNotFoundDescription),
				});
				setContext({ loading: false, error: null, connectedProject });

				return;
			}
			if (err.statusCode === 403) {
				onSuccessDisconnect();
				showFlag({
					type: 'error',
					title: formatMessage(messages.disconnectPermissionsFailFlagTitle),
					description: formatMessage(messages.disconnectPermissionsFailFlagDescription),
				});
				setContext({ loading: false, error: null, connectedProject });

				return;
			}

			setContext({ loading: false, error: err, connectedProject });
			onFailDisconnect('Failed to disconnect project', err);

			throw err;
		}
	}, [
		startDisconnectExperience,
		projectId,
		setContext,
		onSuccessDisconnect,
		connectedProject,
		onFailDisconnect,
		showFlag,
		formatMessage,
	]);

	const onProjectChange = useCallback(
		(
			selectedProject?: PickedProject,
		): Promise<
			| {
					// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
					project: ConnectedProject | null | void;
					isSwitching?: boolean;
					action: typeof ProjectAction.CONNECT | typeof ProjectAction.DISCONNECT;
			  }
			| undefined
		> => (selectedProject ? connectProject(selectedProject) : disconnectProject()),
		[connectProject, disconnectProject],
	);

	return {
		initialised: !loading && !error,
		loading,
		error,
		onProjectChange,
		connectedProject,
	};
};
